import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useVertical } from "@/lib/verticalsApi";
import { useBlogList } from "@/lib/blogApi";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import ArticleCard from "@/components/blog/ArticleCard";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/site/Breadcrumbs";
import { signalPrerenderReady } from "@/lib/prerenderReady";
import DynamicRouteLoader from "@/components/site/DynamicRouteLoader";

function _origin() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

// Derives a human-readable, non-empty title from a route slug for the
// loading-state <Helmet>. Title-Cases hyphen-separated words. Used only
// until real vertical data loads; success branch's Helmet takes over.
function _slugToTitle(slug) {
  if (!slug) return "Vertical";
  return String(slug)
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") || "Vertical";
}

function RichTextRenderer({ node }) {
  if (!node) return null;
  // Payload Lexical: `node.root.children`
  const root = node?.root;
  if (!root) return null;
  return (
    <div className="prose prose-neutral max-w-none">
      {(root.children || []).map((c, i) => renderNode(c, i))}
    </div>
  );
}

function renderNode(n, i) {
  if (!n) return null;
  if (n.type === "paragraph") {
    return <p key={i}>{(n.children || []).map((c, j) => renderInline(c, j))}</p>;
  }
  if (n.type === "heading") {
    const Tag = `h${(n.tag || "h2").replace("h", "")}`;
    return <Tag key={i}>{(n.children || []).map((c, j) => renderInline(c, j))}</Tag>;
  }
  if (n.type === "list") {
    const Tag = n.listType === "number" ? "ol" : "ul";
    return <Tag key={i}>{(n.children || []).map((c, j) => (
      <li key={j}>{(c.children || []).map((cc, k) => renderInline(cc, k))}</li>
    ))}</Tag>;
  }
  return null;
}

function renderInline(n, i) {
  if (!n) return null;
  if (n.type === "text") return <span key={i}>{n.text}</span>;
  if (n.type === "link") {
    const href = n.fields?.url || n.url || "#";
    return <a key={i} href={href} className="underline">
      {(n.children || []).map((c, j) => renderInline(c, j))}
    </a>;
  }
  return null;
}

export default function VerticalHub() {
  const { slug } = useParams();
  const { data: v, loading, error, notFound } = useVertical(slug);
  const { data: ss } = useSiteSettings();
  // Also fetch full article list for this vertical (page 1) to render the
  // "All articles" section below curated pillars/clusters.
  const listQuery = React.useMemo(
    () => ({ page: 1, limit: 24, vertical: slug, search: "", category: "", tag: "" }),
    [slug]);
  const { data: postList } = useBlogList(listQuery);
  const posts = postList?.docs || [];

  // Prerender.io-compatible readiness signal (see /lib/prerenderReady.js
  // and /app/docs/SEO_CONTENT_BIBLE.md §Prerender rules).
  React.useEffect(() => {
    if (loading) return;
    if (notFound) { signalPrerenderReady("notFound"); return; }
    if (error || !v) { signalPrerenderReady("error"); return; }
    signalPrerenderReady("ok");
  }, [loading, error, notFound, v]);

  const siteName = ss?.displayName || ss?.companyName || "MTS";
  // Loading state: MUST NOT mount a page-shell (<main>Loading…</main>)
  // as the first child of #root. See /app/docs/SEO_CONTENT_BIBLE.md §4.5.
  if (loading) {
    // Bing "Add a title tag" fix: renderer may snapshot before data
    // loads. Emit a slug-derived, non-empty <title> + canonical so the
    // captured HTML is never title-less. Data-loaded branch's Helmet
    // still takes over with the full SEO title once vertical resolves.
    const _loadingTitle = _slugToTitle(slug);
    return (
      <>
        <Helmet>
          <title>{`${_loadingTitle} — ${siteName}`}</title>
          <link rel="canonical" href={`${_origin()}/verticals/${slug}`} />
        </Helmet>
        <DynamicRouteLoader variant="loading" testId="vertical-hub-loading" />
      </>
    );
  }
  // Confirmed 404 from API — vertical does not exist. No noindex meta
  // needed here (parent `/verticals` list already lists all valid ones),
  // but keep the message and prevent crawlers from following broken slugs.
  if (notFound) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-16" data-testid="vertical-hub-not-found">
        <Helmet>
          <title>Vertical not found — {siteName}</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <h1 className="text-3xl font-semibold">Vertical not found</h1>
        <p className="mt-4 text-neutral-600">
          <Link className="underline" to="/verticals">Back to all verticals</Link>
        </p>
      </main>
    );
  }
  // Transient failure (network / 5xx / timeout) after retry — vertical
  // MAY exist. DO NOT emit noindex — a prerender snapshot must not
  // permanently poison the crawler-cache for a valid URL.
  // Transient error: same guarantee — no <main> shell in #root. Marker
  // stays on the portal element so smart-poll refuses to flip ready.
  // See /app/docs/SEO_CONTENT_BIBLE.md §4.5.
  if (error || !v) {
    return <DynamicRouteLoader variant="unavailable" testId="vertical-hub-unavailable" />;
  }

  const canonical = `${_origin()}/verticals/${v.slug}`;
  const title = v.seoTitle || `${v.heroTitle || v.name} — ${siteName}`;
  const description = v.seoDescription || v.description || "";
  const heroImage = v.heroImage?.url || null;
  const pillars = v.pillarArticles || [];
  const clusters = v.contentClusters || [];
  const categories = v.categories || [];

  const breadcrumbItems = [
    { name: "Home", href: "/" },
    { name: "Verticals", href: "/verticals" },
    { name: v.name },
  ];
  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbItems, _origin());
  // CollectionPage schema — helps search engines classify hub pages.
  // Enriched with mainEntity=ItemList so AI/search sees the vertical's
  // curated content collection (pillars first, then latest articles).
  // publisher references the site-wide Organization via @id — no dup.
  const collectionItems = [
    ...pillars.map((p) => ({
      "@type": "ListItem",
      name: p.title,
      url: `${_origin()}/blog/${p.slug}`,
    })),
    ...posts.slice(0, 20).map((p) => ({
      "@type": "ListItem",
      name: p.title,
      url: `${_origin()}/blog/${p.slug}`,
    })),
  ].map((li, i) => ({ ...li, position: i + 1 }));
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description: description || undefined,
    url: canonical,
    isPartOf: { "@id": `${_origin()}/#website` },
    publisher: { "@id": `${_origin()}/#organization` },
    about: v.name,
    ...(collectionItems.length > 0 && {
      mainEntity: {
        "@type": "ItemList",
        name: `${v.name} — content collection`,
        numberOfItems: collectionItems.length,
        itemListElement: collectionItems,
      },
    }),
  };

  return (
    <main className="max-w-6xl mx-auto px-4 py-12" data-testid="vertical-hub-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        {(v.articleCount ?? 0) === 0 && <meta name="robots" content="noindex,follow" />}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        {heroImage && <meta property="og:image" content={heroImage} />}
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      </Helmet>

      <Breadcrumbs items={breadcrumbItems} className="mb-6" />

      <header className="mb-10">
        {heroImage && (
          <img src={heroImage} alt={v.name}
               className="w-full h-64 sm:h-80 object-cover rounded-sm mb-8" />
        )}
        <div className="text-xs uppercase tracking-wider text-neutral-500">
          Industry Vertical
        </div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-semibold tracking-tight text-neutral-900"
            data-testid="vertical-hub-title">
          {v.heroTitle || v.name}
        </h1>
        {v.heroSubtitle && (
          <p className="mt-4 text-lg text-neutral-600 max-w-3xl">{v.heroSubtitle}</p>
        )}
        {!v.heroSubtitle && v.description && (
          <p className="mt-4 text-lg text-neutral-600 max-w-3xl">{v.description}</p>
        )}
      </header>

      {v.introduction && (
        <section className="mb-12 max-w-3xl">
          <RichTextRenderer node={v.introduction} />
        </section>
      )}

      {categories.length > 0 && (
        <section className="mb-12" data-testid="vertical-categories-section">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-6">
            Topics in {v.name}
          </h2>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((c) => (
              <li key={c.slug}>
                <Link to={`/blog?vertical=${encodeURIComponent(v.slug)}&category=${encodeURIComponent(c.slug)}`}
                      className="block border border-neutral-200 hover:border-neutral-900 transition p-4 rounded-sm text-sm"
                      data-testid={`vertical-topic-${c.slug}`}>
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pillars.length > 0 && (
        <section className="mb-12" data-testid="vertical-pillars-section">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-6">
            Cornerstone guides
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pillars.map((p) => (
              <li key={p.id || p.slug}>
                <Link to={`/blog/${p.slug}`}
                      className="block border border-neutral-200 hover:border-neutral-900 transition p-6 rounded-sm">
                  <div className="text-xs uppercase tracking-wider text-neutral-500">
                    Pillar
                  </div>
                  <h3 className="mt-2 text-lg font-semibold text-neutral-900">
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <p className="mt-2 text-sm text-neutral-600 line-clamp-3">
                      {p.excerpt}
                    </p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {clusters.map((cl, ci) => (
        <section key={ci} className="mb-12" data-testid={`vertical-cluster-${ci}`}>
          <h2 className="text-2xl font-semibold text-neutral-900 mb-3">
            {cl.title}
          </h2>
          {cl.summary && (
            <p className="text-neutral-600 max-w-3xl mb-6">{cl.summary}</p>
          )}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(cl.articles || []).map((a) => (
              <li key={a.id || a.slug}>
                <Link to={`/blog/${a.slug}`}
                      className="block border border-neutral-200 hover:border-neutral-900 transition p-4 rounded-sm">
                  <div className="text-sm font-medium text-neutral-900">
                    {a.title}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {posts.length > 0 && (
        <section className="mb-12" data-testid="vertical-articles-section">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="text-2xl font-semibold text-neutral-900">
              All {v.name} articles
            </h2>
            {v.articleCount > posts.length && (
              <Link to={`/blog?vertical=${v.slug}`}
                    className="text-sm underline text-neutral-600 hover:text-neutral-900">
                View all {v.articleCount} →
              </Link>
            )}
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <li key={p.id || p.slug}>
                <ArticleCard article={p} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {posts.length === 0 && (
        <section className="mb-12 py-12 border border-dashed border-neutral-300 rounded-sm text-center"
                 data-testid="vertical-empty-state">
          <p className="text-neutral-500">
            No published articles in this vertical yet. Master Research and
            content generation are in progress.
          </p>
        </section>
      )}
    </main>
  );
}
